import { useRef, useEffect, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Clock, Users, Zap } from 'lucide-react';
import {
  getCategoryInfo,
  calculateDistance,
  formatDistance,
  formatActivityDate,
  formatYaarsInAndSpotsLeft,
} from '@utils/helpers';
import ActivitySafetyBadges from '@components/activity/ActivitySafetyBadges';

const CarouselCard = memo(({ activity, isSelected, dist, onSelect, onJoin }) => {
  const spotsLeft = activity.maxParticipants - (activity.participants?.length || 0);
  const isLive = activity.status === 'live' || activity.isQuick;
  const navigate = useNavigate();
  const coverUrl = activity.image && String(activity.image).trim() !== '' ? String(activity.image).trim() : '';

  return (
    <div
      onClick={() => onSelect(activity)}
      className={`shrink-0 w-[290px] snap-center rounded-2xl border bg-white shadow-lg cursor-pointer transition-all duration-200 dark:bg-surface-900 overflow-hidden ${
        coverUrl ? 'p-0' : 'p-3.5'
      } ${
        isSelected
          ? 'border-brand-400 shadow-brand-500/25 ring-1 ring-brand-400/30 scale-[1.02]'
          : 'border-surface-200/60 dark:border-surface-700/60'
      }`}
    >
      {coverUrl && (
        <div
          className="relative h-[92px] w-full shrink-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${coverUrl})` }}
          role="img"
          aria-hidden
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/5 to-black/20" />
          <span className="absolute bottom-2 left-3 text-2xl drop-shadow-md" aria-hidden>
            {activity.emoji || getCategoryInfo(activity.category).emoji}
          </span>
        </div>
      )}

      <div className={coverUrl ? 'px-3.5 pt-2.5 pb-3.5' : ''}>
      <div className="flex items-start gap-3">
        {!coverUrl && (
        <div
          className={`shrink-0 flex h-10 w-10 items-center justify-center rounded-xl text-lg ${
            isLive
              ? 'bg-gradient-to-br from-amber-500/15 to-red-500/15 dark:from-amber-500/20 dark:to-red-500/20'
              : 'bg-gradient-to-br from-brand-500/10 to-violet-500/10 dark:from-brand-500/15 dark:to-violet-500/15'
          }`}
        >
          {activity.emoji || getCategoryInfo(activity.category).emoji}
        </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <h4 className="text-[13px] font-bold text-surface-900 dark:text-white truncate">
              {activity.title}
            </h4>
            {isLive && (
              <span className="shrink-0 inline-flex items-center gap-0.5 rounded-full bg-gradient-to-r from-amber-500 to-red-500 px-1.5 py-0.5 text-[8px] font-bold text-white leading-none">
                <Zap size={7} className="fill-white" />
                LIVE
              </span>
            )}
          </div>

          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[10px] text-surface-500 dark:text-surface-400">
            {dist != null && (
              <span className="inline-flex items-center gap-0.5">
                <MapPin size={9} className="text-brand-400" />
                {formatDistance(dist)}
              </span>
            )}
            <span className="inline-flex items-center gap-0.5">
              <Clock size={9} className="text-violet-400" />
              {formatActivityDate(activity.dateTime)}
            </span>
            <span
              className={`inline-flex min-w-0 items-center gap-0.5 ${
                spotsLeft <= 2 && spotsLeft > 0
                  ? 'text-amber-600 dark:text-amber-400 font-semibold'
                  : ''
              }`}
            >
              <Users
                size={9}
                className={
                  spotsLeft <= 2 && spotsLeft > 0
                    ? 'text-amber-500'
                    : 'text-emerald-400'
                }
              />
              <span className="min-w-0 truncate">
                {formatYaarsInAndSpotsLeft(
                  activity.participants?.length || 0,
                  activity.maxParticipants,
                )}
              </span>
            </span>
          </div>

          <div className="mt-1.5">
            <ActivitySafetyBadges
              activity={activity}
              creator={activity.creator}
            />
          </div>
        </div>
      </div>
      </div>

      <div className={`flex items-center gap-2 border-t border-surface-100 dark:border-surface-800 ${coverUrl ? 'mx-3.5 mb-3.5 mt-1 pt-2.5' : 'mt-3 pt-2.5'}`}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/activity/${activity._id || activity.id}`);
          }}
          className="flex-1 rounded-lg border border-surface-200 bg-surface-50 py-1.5 text-[11px] font-bold text-surface-700 transition-all hover:bg-surface-100 active:scale-[0.97] dark:border-surface-700 dark:bg-surface-800 dark:text-surface-300"
        >
          See the Vibe
        </button>
        {spotsLeft > 0 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onJoin(activity._id || activity.id, {
                verifiedOnly: activity.verifiedOnly === true,
                category: activity.category,
              });
            }}
            className={`flex-1 rounded-lg py-1.5 text-[11px] font-bold text-white transition-all active:scale-[0.97] ${
              isLive
                ? 'bg-gradient-to-r from-amber-500 to-red-500 hover:shadow-md'
                : 'bg-brand-500 hover:bg-brand-600'
            }`}
          >
            Join the Vibe
          </button>
        )}
      </div>
    </div>
  );
});
CarouselCard.displayName = 'CarouselCard';

const MapCardCarousel = ({ activities, selectedActivity, onSelect, onJoin, userLoc }) => {
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!selectedActivity || !scrollRef.current) return;
    const selId = selectedActivity._id || selectedActivity.id;
    const idx = activities.findIndex((a) => (a._id || a.id) === selId);
    if (idx < 0) return;
    const card = scrollRef.current.children[idx];
    if (card) {
      card.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest',
      });
    }
  }, [selectedActivity?._id, activities]);

  if (activities.length === 0) return null;

  return (
    <div className="lg:hidden absolute bottom-0 left-0 right-0 z-10 pb-2">
      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto snap-x snap-mandatory px-4 pb-3 pt-1 scrollbar-hide"
        style={{ scrollPaddingInline: '16px' }}
      >
        {activities.map((activity) => {
          const dist =
            activity.location?.coordinates && userLoc
              ? calculateDistance(
                  userLoc.lat,
                  userLoc.lng,
                  activity.location.coordinates[1],
                  activity.location.coordinates[0]
                )
              : null;

          return (
            <CarouselCard
              key={activity._id || activity.id}
              activity={activity}
              isSelected={
                (selectedActivity?._id || selectedActivity?.id) ===
                (activity._id || activity.id)
              }
              dist={dist}
              onSelect={onSelect}
              onJoin={onJoin}
            />
          );
        })}
      </div>
    </div>
  );
};

export default MapCardCarousel;
